// brand-tokens.js — JS port of the GCBC design-system tokens.
// Source of truth for colour scales, type roles, format helpers, and the
// status-badge tone map. Imported across screens and shells.

export const brand = {
  name: "Gift Card Buyers Club",
  shortName: "GCBC",
  domain: "giftcardbuyersclub.com",
  tagline: "A members-only buying group for gift cards.",
};

export const brandTeal = {
  50: "#F0FDFA", 100: "#CCFBF1",
  700: "#0F766E", 800: "#115E59", 900: "#134E4A",
};

export const neutral = {
  50: "#F8FAFC", 100: "#F1F5F9", 200: "#E2E8F0", 300: "#CBD5E1",
  400: "#94A3B8", 500: "#64748B", 600: "#475569", 700: "#334155",
  800: "#1E293B", 900: "#0F172A",
};

export const semantic = {
  success: { tint: "#ECFDF5", primary: "#10B981", deep: "#047857" },
  warning: { tint: "#FFFBEB", primary: "#F59E0B", deep: "#B45309" },
  danger:  { tint: "#FEF2F2", primary: "#EF4444", deep: "#B91C1C" },
  info:    { tint: "#F0F9FF", primary: "#0EA5E9", deep: "#0369A1" },
  flagged: { tint: "#FFF7ED", primary: "#F97316", deep: "#C2410C" },
};

// ── Status badge tone map — one source of truth across the product ──
export const statusBadgeMap = {
  application: {
    PendingReview: "warning",
    ApprovedAwaitingKYC: "info",
    KYCInProgress: "info",
    KYCPassAwaitingBank: "info",
    KYCFailed: "danger",
    ActiveMember: "success",
    Rejected: "danger",
  },
  deal: {
    Draft: "neutral",
    Review: "warning",
    Published: "success",
    Closed: "neutral",
    Paused: "warning",
  },
  reservation: {
    Active: "info",
    SubmittedPartial: "brand",
    SubmittedFinal: "success",
    ExpiredReleased: "neutral",
    Cancelled: "neutral",
  },
  card: {
    Pending: "neutral",
    Approved: "success",
    FlaggedFormat: "flagged",
    FlaggedDuplicate: "flagged",
    AwaitingMemberResponse: "warning",
    Rejected: "danger",
  },
  submission: {
    Pending: "neutral",
    PendingReview: "flagged",
    Approved: "success",
    Completed: "success",
    PartiallyApproved: "brand",
    Flagged: "flagged",
    FlaggedFormat: "flagged",
    FlaggedDuplicate: "flagged",
    AwaitingMemberResponse: "warning",
    Rejected: "danger",
  },
  payment: {
    Eligible: "info",
    Scheduled: "info",
    Sent: "brand",
    Completed: "success",
    Returned: "danger",
    Refunded: "warning",
    Cancelled: "neutral",
  },
  memberPayment: {
    Eligible: "info",
    Scheduled: "info",
    Sent: "brand",
    Completed: "success",
    Returned: "danger",
    Refunded: "warning",
    Cancelled: "neutral",
  },
  paymentRun: {
    Generated: "info",
    Sent: "brand",
    CompletedPartial: "warning",
    Completed: "success",
  },
  member: {
    Active: "success",
    Suspended: "warning",
    Deactivated: "neutral",
  },
};

export const badgeToneToClass = {
  neutral: { bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400" },
  info:    { bg: "bg-sky-50",    text: "text-sky-700",   dot: "bg-sky-500" },
  brand:   { bg: "bg-brand-50",  text: "text-brand-800", dot: "bg-brand-700" },
  success: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  warning: { bg: "bg-amber-50",  text: "text-amber-700", dot: "bg-amber-500" },
  danger:  { bg: "bg-red-50",    text: "text-red-700",   dot: "bg-red-500" },
  flagged: { bg: "bg-orange-50", text: "text-orange-700",dot: "bg-orange-500" },
};

// Friendly label for an enum value ("PendingReview" → "Pending Review")
export function humanizeStatus(s) {
  if (!s) return "";
  return s
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/(KYC)/g, " KYC ")
    .replace(/\s+/g, " ")
    .trim();
}

// ── Format helpers ──
export const format = {
  currency(amount) {
    if (amount == null || isNaN(amount)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency", currency: "USD",
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(amount);
  },
  currencyCompact(amount) {
    if (amount == null || isNaN(amount)) return "$0";
    if (Math.abs(amount) >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
    if (Math.abs(amount) >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
    return `$${amount.toFixed(0)}`;
  },
  date(d) {
    if (!d) return "";
    const date = typeof d === "string" ? new Date(d) : d;
    return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
  },
  dateTime(d) {
    if (!d) return "";
    const date = typeof d === "string" ? new Date(d) : d;
    const datePart = format.date(date);
    const timePart = new Intl.DateTimeFormat("en-US", {
      hour: "numeric", minute: "2-digit", hour12: true,
      timeZone: "America/New_York", timeZoneName: "short",
    }).format(date);
    return `${datePart} \u00b7 ${timePart}`;
  },
  relative(d) {
    if (!d) return "";
    const date = typeof d === "string" ? new Date(d) : d;
    const diff = (Date.now() - date.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 172800) return "Yesterday";
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return format.date(date);
  },
  cardLast4(cardNumber) {
    if (!cardNumber) return "\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022";
    return `\u2022\u2022\u2022\u2022 ${String(cardNumber).slice(-4)}`;
  },
  bankLast4(accountNumber) {
    if (!accountNumber) return "\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022";
    return `\u2022\u2022\u2022\u2022 ${String(accountNumber).slice(-4)}`;
  },
  phone(e164) {
    if (!e164) return "";
    const digits = String(e164).replace(/\D/g, "");
    const ten = digits.length > 10 ? digits.slice(-10) : digits;
    if (ten.length !== 10) return e164;
    return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  },
  phoneAdminMasked(e164) {
    if (!e164) return "";
    const digits = String(e164).replace(/\D/g, "");
    return `(\u2022\u2022\u2022) \u2022\u2022\u2022 -${digits.slice(-4)}`;
  },
  count(n) {
    if (n == null || isNaN(n)) return "0";
    return new Intl.NumberFormat("en-US").format(n);
  },
  percent(n, decimals = 1) {
    if (n == null || isNaN(n)) return "0%";
    return Number.isInteger(n) ? `${n}%` : `${n.toFixed(decimals)}%`;
  },
};
